import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import echarts from './config'

/**
 * InlineChart - A direct eCharts configuration component
 *
 * Unlike ChartProvider which uses a component-based API, InlineChart accepts
 * a complete eCharts configuration object. This is ideal for AI-generated
 * visualizations or when you need full control over the chart configuration.
 *
 * @example
 * <InlineChart
 *   options={{
 *     xAxis: { type: 'category', data: ['Mon', 'Tue', 'Wed'] },
 *     yAxis: { type: 'value' },
 *     series: [{ type: 'line', data: [120, 200, 150] }]
 *   }}
 *   height={300}
 * />
 */
function InlineChart ({
  options,
  height = 300,
  width,
  className,
  style,
  theme,
  loading = false,
  loadingConfig,
  onChartReady,
  onEvents,
  autoResize = true,
  ...restProps
}) {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const [isReady, setIsReady] = useState(false)

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return

    const chart = echarts.init(containerRef.current, theme, {
      renderer: 'canvas',
      useDirtyRect: true
    })

    chartRef.current = chart
    setIsReady(true)

    if (onChartReady) {
      onChartReady(chart)
    }

    return () => {
      chart.dispose()
      chartRef.current = null
      setIsReady(false)
    }
  }, [theme, onChartReady])

  // Update options
  useEffect(() => {
    if (!chartRef.current || !options) return

    try {
      chartRef.current.setOption(options, true)
    } catch (error) {
      console.error('Failed to set chart options:', error)
    }
  }, [options])

  // Handle loading state
  useEffect(() => {
    if (!chartRef.current) return

    if (loading) {
      chartRef.current.showLoading('default', loadingConfig || {
        text: 'Loading...',
        textColor: '#000',
        maskColor: 'rgba(255, 255, 255, 0.8)'
      })
    } else {
      chartRef.current.hideLoading()
    }
  }, [loading, loadingConfig])

  // Handle resize
  useEffect(() => {
    if (!chartRef.current || !autoResize) return

    const handleResize = () => {
      chartRef.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    // Also observe container size changes
    const resizeObserver = new ResizeObserver(handleResize)
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [autoResize, isReady])

  // Handle events
  useEffect(() => {
    if (!chartRef.current || !onEvents) return

    const eventHandlers = []

    Object.entries(onEvents).forEach(([eventName, handler]) => {
      chartRef.current.on(eventName, handler)
      eventHandlers.push({ eventName, handler })
    })

    return () => {
      eventHandlers.forEach(({ eventName, handler }) => {
        chartRef.current?.off(eventName, handler)
      })
    }
  }, [onEvents, isReady])

  const containerStyle = {
    width: width || '100%',
    height,
    ...style
  }

  return (
    <div
      ref={containerRef}
      className={clsx('echarts-inline-chart', className)}
      style={containerStyle}
      {...restProps}
    />
  )
}

InlineChart.propTypes = {
  /**
   * Complete eCharts configuration object
   * @see https://echarts.apache.org/en/option.html
   */
  options: PropTypes.object.isRequired,

  /**
   * Chart height (number for pixels or string for other units)
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Chart width (number for pixels or string for other units)
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /**
   * Additional CSS classes
   */
  className: PropTypes.string,

  /**
   * Inline styles
   */
  style: PropTypes.object,

  /**
   * ECharts theme name or theme object
   */
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

  /**
   * Loading state
   */
  loading: PropTypes.bool,

  /**
   * Loading animation configuration
   */
  loadingConfig: PropTypes.object,

  /**
   * Callback when chart instance is ready
   */
  onChartReady: PropTypes.func,

  /**
   * Event handlers map { eventName: handler }
   */
  onEvents: PropTypes.object,

  /**
   * Auto resize on window resize
   */
  autoResize: PropTypes.bool
}

export default InlineChart
